import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Package, Truck, MapPin, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    // Clear pending order from localStorage
    localStorage.removeItem('pending_order');
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Order not found</h2>
          <Link to="/shop">
            <Button>Continue Shopping</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-confirmation-page py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Order Confirmed!
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Thank you for your order, {order.customer_info?.first_name}!
          </p>
          <p className="text-gray-600">
            Order Number: <strong>#{order.order_number}</strong>
          </p>
        </div>

        {/* Order Status */}
        <Card className="mb-8 border-2 border-green-100">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Order Status</h2>
                <p className="text-gray-600">We've sent a confirmation email to {order.customer_email}</p>
              </div>
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full font-semibold capitalize">
                {order.status}
              </span>
            </div>

            {/* Order Timeline */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Order Placed</h3>
                  <p className="text-sm text-gray-600">
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  order.payment_status === 'completed'
                    ? 'bg-green-600'
                    : 'bg-gray-200'
                }`}>
                  <CheckCircle className={`h-6 w-6 ${
                    order.payment_status === 'completed'
                      ? 'text-white'
                      : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Payment Confirmed</h3>
                  <p className="text-sm text-gray-600">
                    {order.payment_status === 'completed'
                      ? 'Payment successful'
                      : 'Processing payment'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                  <Package className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Processing</h3>
                  <p className="text-sm text-gray-600">Your order is being prepared</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                  <Truck className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Out for Delivery</h3>
                  <p className="text-sm text-gray-600">We'll notify you when shipped</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Items</h2>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={item.product_id || item._id || `oc-item-${index}`} className="flex justify-between items-center pb-4 border-b last:border-b-0">
                  <div>
                    <h3 className="font-bold text-gray-900">{item.product_name}</h3>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      ₦{item.total_price.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      ₦{item.unit_price.toLocaleString()} each
                    </p>
                  </div>
                </div>
              ))}

              <div className="pt-4 space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span className="font-semibold">₦{order.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Delivery Fee</span>
                  <span className="font-semibold">₦{order.delivery_fee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t">
                  <span>Total</span>
                  <span className="text-green-600">₦{order.total_amount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Address */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Delivery Address</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-600 mt-1" />
                <div>
                  <p className="text-gray-700">{order.delivery_address.street}</p>
                  <p className="text-gray-700">
                    {order.delivery_address.city}, {order.delivery_address.state}
                  </p>
                  <p className="text-gray-700">{order.delivery_address.region}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-600" />
                <p className="text-gray-700">{order.delivery_address.phone}</p>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-600" />
                <p className="text-gray-700">{order.customer_email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop">
            <Button size="lg" className="w-full sm:w-auto">
              Continue Shopping
            </Button>
          </Link>
          <Link to="/my-orders">
            <Button size="lg" variant="outline" className="w-full sm:w-auto">
              View All Orders
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;